import React from 'react'
import { Box, Typography, Button, Paper, Alert } from '@mui/material'
import {
    Error as ErrorIcon,
    Refresh as RefreshIcon,
    Home as HomeIcon
} from '@mui/icons-material'

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        }
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true }
    }

    componentDidCatch(error, errorInfo) {
        // Log the error to console in development
        console.error('Error caught by ErrorBoundary:', error, errorInfo)

        this.setState({
            error: error,
            errorInfo: errorInfo
        })
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null })
    }

    handleGoHome = () => {
        window.location.href = '/'
    }

    render() {
        if (this.state.hasError) {
            // Custom fallback UI if provided
            if (this.props.fallback) {
                return this.props.fallback
            }

            // Default fallback UI
            return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: this.props.fullPage ? '100vh' : '400px',
                        p: 3,
                        bgcolor: this.props.fullPage ? '#f5f5f5' : 'transparent',
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            p: 4,
                            maxWidth: 600,
                            textAlign: 'center',
                            borderRadius: 2,
                        }}
                    >
                        <ErrorIcon
                            sx={{
                                fontSize: 72,
                                color: 'error.main',
                                mb: 2
                            }}
                        />

                        <Typography variant="h4" gutterBottom fontWeight="bold" color="error">
                            Oops! Something went wrong
                        </Typography>

                        <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                            We're sorry for the inconvenience. The application encountered an unexpected error.
                            {this.props.fullPage
                                ? ' Please try refreshing the page or go back to the home page.'
                                : ' You can try again or continue using other parts of the application.'}
                        </Typography>

                        {/* Show error details in development mode only */}
                        {import.meta.env.DEV && this.state.error && (
                            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                    Error Details (Development Only):
                                </Typography>
                                <Paper
                                    sx={{
                                        p: 2,
                                        mt: 1,
                                        bgcolor: '#fff3f3',
                                        maxHeight: 200,
                                        overflow: 'auto',
                                        border: '1px solid #ffcdd2'
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        component="pre"
                                        sx={{
                                            fontSize: '0.75rem',
                                            fontFamily: 'monospace',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        {this.state.error.toString()}
                                        {this.state.errorInfo?.componentStack && (
                                            <>
                                                {'\n\nComponent Stack:'}
                                                {this.state.errorInfo.componentStack}
                                            </>
                                        )}
                                    </Typography>
                                </Paper>
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={this.handleReset}
                                size="large"
                            >
                                Try Again
                            </Button>

                            {this.props.fullPage && (
                                <Button
                                    variant="outlined"
                                    startIcon={<HomeIcon />}
                                    onClick={this.handleGoHome}
                                    size="large"
                                >
                                    Go to Home
                                </Button>
                            )}
                        </Box>

                        {!import.meta.env.DEV && (
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 3, display: 'block' }}>
                                If this problem persists, please contact support.
                            </Typography>
                        )}
                    </Paper>
                </Box>
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary
